import { quoteLinkTyped } from '../documents/quoteLink';
import { isEmailAddress } from '../validators';

/**
 * 🔒 One-click Unsubscribe (piece 32; T354; decision 2640; RFC 2369 and RFC 8058): the target a received message's
 * List-Unsubscribe header names, read from the stored header block when the pane opens and kept in no column.
 * 🔨 An https target opens in the person's own browser and a mailto target sends one request from the mailbox the
 * message arrived in; the server never reaches an https target, and a plain http target is never offered.
 */
export type MailUnsubscribe = { kind: 'web'; url: string; host: string } | { kind: 'mail'; address: string; subject: string; body: string };

/** The answer the reading pane draws from: what the press does, without the mailto subject and body the server keeps. */
export type MailUnsubscribeView = { kind: 'web'; url: string; host: string } | { kind: 'mail'; address: string };

/** The route a mailto target's request is sent through. */
export const mailUnsubscribePath = (id: string): string => `/mail/messages/${id}/unsubscribe`;

/** The answer to the request: Sent, or Held under the person's undo delay, and where it went. */
export interface MailUnsubscribeAnswer {
  outcome: 'Sent' | 'Held' | 'Failed';
  address: string;
}

/** The words a request carries where the target names none. */
export const UNSUBSCRIBE_DEFAULT_TEXT = 'unsubscribe';

/** One header's value from a stored header block, its folded lines joined; null where the block has none. */
export function rawHeaderValue(rawHeaders: string, name: string): string | null {
  const lines = rawHeaders.replace(/\r\n/g, '\n').split('\n');
  const wanted = `${name.toLowerCase()}:`;
  for (let i = 0; i < lines.length; i += 1) {
    if (!lines[i]!.toLowerCase().startsWith(wanted)) continue;
    let value = lines[i]!.slice(wanted.length);
    while (i + 1 < lines.length && /^[ \t]/.test(lines[i + 1]!)) {
      i += 1;
      value += ` ${lines[i]!.trim()}`;
    }
    return value.trim();
  }
  return null;
}

function decoded(text: string): string {
  try {
    // 🔨 A mailto target is percent-encoded alone (RFC 6068): a plus is a plus, as in a list's own address.
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

/** A mailto target as one address, its subject and its body; null where it names no single address. */
function mailtoOf(uri: string): Extract<MailUnsubscribe, { kind: 'mail' }> | null {
  const rest = uri.slice('mailto:'.length);
  const [to, query = ''] = rest.split('?', 2) as [string, string?];
  const address = decoded(to).trim();
  if (!isEmailAddress(address)) return null;
  const params = new Map<string, string>();
  for (const pair of query.split('&')) {
    const at = pair.indexOf('=');
    if (at > 0) params.set(pair.slice(0, at).toLowerCase(), decoded(pair.slice(at + 1)));
  }
  // 🔒 A percent-decoded line break would become a header line in the draft's subject, so every control character reads as a space (T1950).
  // 🔒 The one cleaner the quote link's typed text goes through also drops format characters, a direction override among them (T2002).
  // The 200 cap counts code points, so a surrogate pair is never cut in half (decision 2723; T2006).
  const subject = [...quoteLinkTyped(params.get('subject') ?? '')].slice(0, 200).join('') || UNSUBSCRIBE_DEFAULT_TEXT;
  const body = (params.get('body') ?? '').trim().slice(0, 2000) || UNSUBSCRIBE_DEFAULT_TEXT;
  return { kind: 'mail', address, subject, body };
}

function webOf(uri: string): Extract<MailUnsubscribe, { kind: 'web' }> | null {
  try {
    const url = new URL(uri);
    if (url.protocol !== 'https:' || url.hostname === '' || url.username !== '' || url.password !== '') return null;
    return { kind: 'web', url: url.toString(), host: url.hostname };
  } catch {
    return null;
  }
}

/**
 * 🔒 The target the press goes to: an https target the List-Unsubscribe-Post header marks One-Click first, then a
 * mailto target, then any other https target; null where the header names none of these.
 */
export function listUnsubscribeOf(rawHeaders: string): MailUnsubscribe | null {
  const header = rawHeaderValue(rawHeaders, 'List-Unsubscribe');
  if (!header) return null;
  const uris = [...header.matchAll(/<([^>]*)>/g)].map((m) => m[1]!.trim());
  const oneClick = /list-unsubscribe\s*=\s*one-click/i.test(rawHeaderValue(rawHeaders, 'List-Unsubscribe-Post') ?? '');
  const webs = uris.filter((u) => /^https:/i.test(u)).map(webOf).filter((w): w is Extract<MailUnsubscribe, { kind: 'web' }> => w !== null);
  const mails = uris.filter((u) => /^mailto:/i.test(u)).map((u) => mailtoOf(`mailto:${u.slice(7)}`)).filter((m): m is Extract<MailUnsubscribe, { kind: 'mail' }> => m !== null);
  if (oneClick && webs[0]) return webs[0];
  return mails[0] ?? webs[0] ?? null;
}

/** What the pane is told: the web target whole, the mail target's address alone. */
export function unsubscribeView(target: MailUnsubscribe | null): MailUnsubscribeView | null {
  if (!target) return null;
  return target.kind === 'web' ? target : { kind: 'mail', address: target.address };
}

/** 🔨 Whether a screening outcome carries a warning band: any outcome but Passed, and none where the message was never screened (T1950). */
export function screeningWarned(outcome: string | null | undefined): boolean {
  return !!outcome && outcome !== 'Passed';
}

/**
 * 🔨 Whether Unsubscribe stands at all: on received mail alone, never on a held message nor on one carrying a warning
 * band, because the link in a forged list message is the trap.
 */
export function unsubscribeOffered(facts: { direction: 'Inbound' | 'Outbound'; folderKind: string; warned: boolean }): boolean {
  return facts.direction === 'Inbound' && facts.folderKind !== 'Quarantine' && !facts.warned;
}
